<script setup>
const props = defineProps({
  name: {
    type: String,
    default: 'name',
  },
  label: {
    type: String,
    required: true,
  },
  fileTypes: {
    type: Array,
    default: () => [],
  },
  fileTypesAccept: {
    type: Array,
    default: undefined,
  },
});

const model = defineModel({ type: Object, default: null });

const { value, errorMessage, handleChange } = useField(() => props.name, null, {
  initialValue: model.value,
  validateOnValueUpdate: false,
});

useSyncModels(model, value);

const inputRef = ref(null);
const { focused } = useFocus(inputRef);

const { t } = useI18n();

const accept = computed(() =>
  (props.fileTypesAccept || props.fileTypes)
    .map((fileType) => `.${fileType}`)
    .join(','),
);

const placeholder = computed(() =>
  t('form.placeholders.file', { types: props.fileTypes.join(', ') }),
);

const fileName = ref('');

function change(event) {
  const file = event.target.files?.[0];
  if (!file) return;

  handleChange(file);
}

watch(value, (value) => {
  fileName.value = value ? value.name : '';
});
</script>

<template>
  <UiFormInputBase
    class="form-file"
    :label="label"
    :is-active="focused"
    :error="errorMessage"
  >
    <input
      ref="inputRef"
      type="file"
      hidden
      :accept="accept"
      @change="change"
    />

    <div class="form-file__value">
      <CIcon
        name="attachment"
        class="form-file__icon form-file__icon--attachment"
      />

      <span
        class="form-file__font form-file__font--value"
        :class="{ 'form-file__font--crop': fileName }"
      >
        {{ fileName || placeholder }}
      </span>
    </div>
  </UiFormInputBase>
</template>

<style scoped lang="scss">
.form-file {
  cursor: pointer;

  &__value {
    display: flex;
    align-items: center;
    gap: em(8);
    color: $color-red-1;
  }

  &__font {
    &--value {
      font-size: em(18);
      line-height: em(20, 18);

      @include media-breakpoint-down(sm) {
        font-size: em(16);
        line-height: em(18, 16);
      }
    }

    &--crop {
      @include text-overflow;
    }
  }

  &__icon {
    &--attachment {
      flex-shrink: 0;
      width: em(14);
      height: em(27);
      margin: em(-3.5) 0;

      @include media-breakpoint-down(sm) {
        width: em(12);
        height: em(23);
        margin: em(-2.5) 0;
      }
    }
  }
}
</style>
